/**
 * English Translations for Al Ummah Now App
 *
 * Complete set of English UI strings for the application.
 */

import type { AppTranslations } from '../types';

export const EN_TRANSLATIONS: AppTranslations = {
  common: {
    appName: 'Al Ummah Now',
    loading: 'Loading...',
    error: 'Error',
    success: 'Success',
    cancel: 'Cancel',
    save: 'Save',
    delete: 'Delete',
    edit: 'Edit',
    create: 'Create',
    search: 'Search',
    filter: 'Filter',
    sort: 'Sort',
    close: 'Close',
    back: 'Back',
    backTo: 'Back to {page}',
    next: 'Next',
    previous: 'Previous',
    submit: 'Submit',
    confirm: 'Confirm',
    noResults: 'No results found',
    view: 'View',
    viewAll: 'View All',
    learnMore: 'Learn More',
    retry: 'Retry',
    demo: 'Demo',
    welcome: 'Welcome',
    collapse: 'Collapse',
    expand: 'Expand',
    loadMore: 'Load More',
    saving: 'Saving...',
    saved: 'Saved!',
    saveFailed: 'Save Failed',
  },

  languages: {
    english: 'English',
    arabic: 'Arabic',
  },

  header: {
    noChannelSelected: 'No Channel Selected',
    switchAccount: 'Switch Account',
    addAccount: 'Add Account',
    logout: 'Logout',
    logoutAll: 'Logout from All Accounts',
    settings: 'Settings',
    darkMode: 'Dark Mode',
    lightMode: 'Light Mode',
    viewChannel: 'View Channel',
  },

  navigation: {
    navigationMenu: 'Navigation menu',
    navigation: 'NAVIGATION',
    accounts: 'ACCOUNTS',
    profile: 'Profile',
    analytics: 'Analytics',
    noChannel: 'No Channel',
    noActiveChannel: 'No active channel',
    createChannel: 'Create Channel',
    channelName: 'Channel Name',
    enterChannelName: 'Enter channel name',
    addAnotherAccount: 'Add another account',
    signOutOf: 'Sign out of',
    signOutOfAll: 'Sign out of all accounts',
    signIn: 'Sign In',
    home: 'Go Home',
    light: 'Light',
    dark: 'Dark',
    channel: 'channel',
    channels: 'channels',
  },

  channel: {
    myChannels: 'My Channels',
    selectChannel: 'Select Channel',
    createChannel: 'Create Channel',
    editChannel: 'Edit Channel',
    deleteChannel: 'Delete Channel',
    channelName: 'Channel Name',
    channelDescription: 'Channel Description',
    channelAvatar: 'Channel Avatar',
    channelBanner: 'Channel Banner',
    mainChannel: 'Main Channel',
    subscribers: 'Subscribers',
    videos: 'Videos',
    subscribe: 'Subscribe',
    subscribed: 'Subscribed',
    unsubscribe: 'Unsubscribe',
    manageChannels: 'Manage Channels',
    yourChannels: 'Your Channels',
    browseWithoutChannel: 'Browse without a channel',
    enterChannelNamePlaceholder: 'Enter channel name',
    refreshChannelList: 'Refresh channel list',
    singleChannel: 'Channel',
    clickToChangeCover: 'Click to change cover',
    clickToChangeAvatar: 'Click to change avatar',
    updateChannelInformation: 'Update your channel information',
    channelNotFoundMessage: "The channel you're trying to edit doesn't exist.",
    charactersRemaining: 'characters',
    saveChanges: 'Save Changes',
    channelNotFound: 'Channel Not Found',
    channelNameEnglish: 'Channel Name (English)',
    channelNameArabic: 'Channel Name (Arabic)',
    channelDescriptionEnglish: 'Channel Description (English)',
    channelDescriptionArabic: 'Channel Description (Arabic)',
    channelTrailer: 'Channel Trailer',
    noChannelTrailer: 'No channel trailer available',
    about: 'About',
    content: 'Content',
    channelSectionsPlaceholder: 'Channel sections will be displayed here',
    // About page
    description: 'Description',
    noDescription: 'No description provided.',
    statistics: 'Statistics',
    joined: 'Joined',
    links: 'Links',
    twitter: 'Twitter',
    facebook: 'Facebook',
    instagram: 'Instagram',
    channelDetails: 'Channel Details',
    channelId: 'Channel ID',
    category: 'Category',
    tags: 'Tags',
    // Channel Tabs
    home: 'Home',
    shorts: 'Shorts',
    live: 'Live',
    playlists: 'Playlists',
    community: 'Community',
    channels: 'Channels',
    manageTabs: 'Manage Tabs',
    setAsDefault: 'Set as Default',
    hideTab: 'Hide Tab',
    showTab: 'Show Tab',
    reorderTabs: 'Reorder Tabs',
    customTab: 'Custom Tab',
    addCustomTab: 'Add Custom Tab',
    tabName: 'Tab Name',
    tabContent: 'Tab Content',
    tabs: {
      manage_tabs: 'Manage Channel Tabs',
      manage_tabs_description: 'Customize which tabs appear on your channel and set the default view',
      toggle_visibility: 'Show/hide this tab',
      set_as_default: 'Set as default tab',
      cannot_hide_last_tab: 'Cannot hide the last visible tab',
      cannot_hide_default_tab: 'Cannot hide the default tab. Please set another tab as default first.',
      default_tab: 'Default Tab',
      visible: 'Visible',
      hidden: 'Hidden',
      save_changes: 'Save Changes',
      changes_saved: 'Tab configuration saved successfully',
      save_failed: 'Failed to save tab configuration',
      drag_to_reorder: 'Drag to reorder tabs',
      at_least_one_visible: 'At least one tab must be visible',
      default_must_be_visible: 'Default tab must be visible',
    },
    // Channel Sections
    seeAll: 'See all',
    recentUploads: 'Recent uploads',
    recentUploadsDescription: 'Your latest videos',
    popularUploads: 'Popular uploads',
    popularUploadsDescription: 'Your most-viewed videos',
    allPlaylists: 'All playlists',
    allPlaylistsDescription: 'All your public playlists',
    featuredVideos: 'Featured videos',
    featuredVideosDescription: 'Highlighted videos from this channel',
    likedVideos: 'Liked videos',
    latestLive: 'Latest live',
    featuredPlaylists: 'Featured playlists',
    featuredPlaylistsDescription: 'Specific playlists you choose',
    singlePlaylist: 'Single playlist',
    singlePlaylistDescription: 'One featured playlist',
    playlistNotFound: 'Playlist not found',
    noPlaylistsFound: 'No playlists found',
    liveNow: 'Live now',
    liveNowDescription: 'Current live streams',
    pastLiveStreams: 'Past live streams',
    pastLiveStreamsDescription: 'Previous broadcasts',
    upcomingLiveStreams: 'Upcoming',
    upcomingLiveStreamsDescription: 'Scheduled live streams',
    featuredChannels: 'Featured channels',
    featuredChannelsDescription: 'Channels you recommend',
    subscriptionsSection: 'Subscriptions',
    subscriptionsSectionDescription: 'Channels you subscribe to',
    addSection: 'Add section',
    configureSection: 'Configure section',
    addChannelSection: 'Add channel section',
    sectionTitle: 'Section title',
    enterSectionTitle: 'Enter section title',
    charactersCount: '{count}/100 characters',
    playlistIds: 'Playlist IDs',
    enterOnePlaylistId: 'Enter one playlist ID',
    enterPlaylistIds: 'Enter playlist IDs separated by commas',
    enterExactlyOnePlaylistId: 'Enter exactly one playlist ID',
    separatePlaylistIds: 'Separate multiple playlist IDs with commas',
    channelIds: 'Channel IDs',
    enterChannelIds: 'Enter channel IDs separated by commas',
    separateChannelIds: 'Separate multiple channel IDs with commas',
    automaticContentMessage: 'This section type will automatically display content from your channel.\nNo additional configuration is needed.',
    creating: 'Creating...',
    createSection: 'Create section',
    automaticContent: 'Automatic content',
    playlist: 'playlist',
    aboutChannel: 'About Channel',
    forYou: 'For You',
    additionalSettings: 'Additional Settings',
    manageSections: 'Manage Sections',
    customizeChannelLayout: 'Customize your channel layout',
    communitySettings: 'Community Posts',
    manageCommunityPosts: 'Manage community posts',
    // Settings
    channelSettings: 'Channel Settings',
    generalSettings: 'General Settings',
    editChannelDetails: 'Edit channel name, description, and branding',
    backToChannel: 'Back to Channel',
  },

  profile: {
    myProfile: 'My Profile',
    createProfile: 'Create Profile',
    editProfile: 'Edit Profile',
    displayName: 'Display Name',
    email: 'Email',
    bio: 'Bio',
    avatar: 'Avatar',
    profileCreated: 'Profile created successfully',
    profileUpdated: 'Profile updated successfully',
    checkingProfile: 'Checking profile...',
    redirectingToProfileCreation: 'Redirecting to profile creation...',
    redirecting: 'Redirecting...',
    welcomeMessage: 'Welcome to Al Ummah Now! Let\'s set up your profile to get started.',
    welcomeUser: 'Welcome {name}!',
    editProfileDescription: 'Edit your profile information below.',
    emailCannotBeChanged: 'Your email address cannot be changed',
    firstName: 'First Name',
    lastName: 'Last Name',
    yourFirstName: 'Your first name',
    yourLastName: 'Your last name',
    firstNameArabic: 'الاسم الأول',
    lastNameArabic: 'اسم العائلة',
    displayNameOptional: 'How others will see your name (optional)',
    displayNameArabic: 'كيف سيرى الآخرون اسمك (اختياري)',
    displayNameEmpty: 'If left empty, we\'ll use your first and last name',
    bioOptional: 'Tell us about yourself (optional)',
    bioArabic: 'أخبرنا عن نفسك (اختياري)',
    privacyNotice: 'Privacy Notice',
    privacyNoticeText: 'Your profile information will be used to personalize your experience on Al Ummah Now. You can update your profile information at any time in your profile settings.',
    welcome: 'Welcome',
    editInstructions: 'Click the edit icon to update your profile information',
    myPlaylists: 'My Playlists',
    playlistsDescription: 'Create and manage your video playlists',
    newPlaylist: 'New Playlist',
    firstPlaylistMessage: 'Create your first playlist to organize your favorite videos',
    noPlaylistsAvailable: 'No playlists available at this time',
    timezone: 'Timezone',
    timezoneDetected: 'Detected timezone',
    currentTimezone: 'Current timezone',
  },

  video: {
    videos: 'Videos',
    uploadVideo: 'Upload Video',
    editVideo: 'Edit Video',
    deleteVideo: 'Delete Video',
    videoTitle: 'Video Title',
    videoDescription: 'Video Description',
    videoThumbnail: 'Video Thumbnail',
    videoDuration: 'Duration',
    videoViews: 'Views',
    videoLikes: 'Likes',
    videoComments: 'Comments',
    videoShares: 'Shares',
    relatedVideos: 'Related Videos',
    watchLater: 'Watch Later',
    addToPlaylist: 'Add to Playlist',
    noVideoSelected: 'No Video Selected',
    selectVideoMessage: 'Select a video from the list to watch',
    // Video Chapters
    chapters: 'Chapters',
    chapter: 'Chapter',
    noChapters: 'No chapters available',
    currentChapter: 'Current chapter',
    nextChapter: 'Next chapter',
    previousChapter: 'Previous chapter',
    addChapter: 'Add Chapter',
    editChapter: 'Edit Chapter',
    deleteChapter: 'Delete Chapter',
    chapterTitle: 'Chapter Title',
    chapterTimestamp: 'Timestamp',
    parseFromDescription: 'Parse from Description',
    showChapters: 'Show Chapters',
    hideChapters: 'Hide Chapters',
    chapterList: 'Chapter List',
    parseChapters: 'Parse Chapters',
    chapterParserHelp: 'Supported formats: MM:SS or HH:MM:SS followed by chapter title',
    chapterParserExample: 'Example: 0:00 Introduction or 1:23:45 Main Topic',
    chaptersDetected: 'chapters detected',
    chaptersParsed: 'Chapters parsed successfully',
    invalidChapterFormat: 'Invalid chapter format',
    firstChapterMustBeZero: 'First chapter must start at 0:00',
    chapterTooClose: 'Chapters must be at least 10 seconds apart',
    maxChaptersReached: 'Maximum 100 chapters allowed',
    duplicateTimestamp: 'Duplicate timestamp detected',
    chapterTitleRequired: 'Chapter title is required',
    chapterTitleMaxLength: 'Chapter title must be 200 characters or less',
    moveChapterUp: 'Move chapter up',
    moveChapterDown: 'Move chapter down',
    previewChapters: 'Preview Chapters',
    autoParseChapters: 'Auto-parse Chapters',
    manualChapters: 'Manual Chapters',
    importedChapters: 'Imported Chapters',
    // Channel videos page
    noVideosAvailable: 'No videos available',
    latest: 'Latest',
    popular: 'Popular',
    oldest: 'Oldest',
    views: 'views',
    view: 'view',
    // Date formatting
    today: 'Today',
    yesterday: 'Yesterday',
    daysAgo: '{count} days ago',
    weeksAgo: '{count} weeks ago',
    monthsAgo: '{count} months ago',
    yearsAgo: '{count} years ago',
    // Video Management Dashboard
    myVideos: 'My Videos',
    manageVideos: 'Manage Videos',
    manageVideosDescription: 'Manage your channel videos',
    videoManagement: 'Video Management',
    allVideos: 'All Videos',
    uploadedVideos: 'Uploaded Videos',
    searchVideos: 'Search videos...',
    filterByStatus: 'Filter by status',
    filterByPrivacy: 'Filter by privacy',
    sortVideos: 'Sort videos',
    allStatus: 'All Status',
    ready: 'Ready',
    processing: 'Processing',
    uploading: 'Uploading',
    failed: 'Failed',
    public: 'Public',
    private: 'Private',
    makePublic: 'Make Public',
    makePrivate: 'Make Private',
    sortByDate: 'Date',
    sortByViews: 'Views',
    sortByTitle: 'Title',
    bulkActions: 'Bulk Actions',
    selectAll: 'Select All',
    deselectAll: 'Deselect All',
    deleteSelected: 'Delete Selected',
    changePrivacy: 'Change Privacy',
    videosSelected: '{count} video(s) selected',
    confirmDelete: 'Are you sure you want to delete this video? This action cannot be undone.',
    confirmDeleteTitle: 'Delete Video?',
    confirmDeleteMultiple: 'Are you sure you want to delete {count} video(s)? This action cannot be undone.',
    confirmDeleteMultipleTitle: 'Delete Multiple Videos?',
    deleteSuccess: 'Video deleted successfully',
    deleteMultipleSuccess: '{count} videos deleted successfully',
    noVideosFound: 'No videos found',
    uploadFirstVideo: 'Upload your first video to get started',
    // Video Edit
    editVideoDetails: 'Edit Video Details',
    videoPreview: 'Video Preview',
    titleEnglish: 'Title (English)',
    titleArabic: 'Title (Arabic)',
    descriptionEnglish: 'Description (English)',
    descriptionArabic: 'Description (Arabic)',
    thumbnailSelector: 'Thumbnail',
    autoGeneratedThumbnails: 'Auto-generated Thumbnails',
    customThumbnail: 'Custom Thumbnail',
    uploadThumbnail: 'Upload Thumbnail',
    selectThumbnail: 'Select Thumbnail',
    dragDropThumbnail: 'Drag and drop an image, or click to browse',
    thumbnailRequirements: 'Recommended: 1280x720 (16:9), Max 2MB',
    setAsDefault: 'Set as Default',
    currentThumbnail: 'Current Thumbnail',
    videoTags: 'Tags',
    addTag: 'Add Tag',
    enterTag: 'Enter a tag',
    removeTag: 'Remove tag',
    videoCategory: 'Category',
    selectCategory: 'Select a category',
    videoLanguage: 'Language',
    videoPrivacy: 'Privacy',
    publicVideo: 'Public - Anyone can watch',
    privateVideo: 'Private - Only you can watch',
    saveChanges: 'Save Changes',
    saving: 'Saving...',
    discardChanges: 'Discard Changes',
    unsavedChanges: 'You have unsaved changes',
    unsavedChangesMessage: 'Are you sure you want to leave? Your changes will be lost.',
    leaveWithoutSaving: 'Leave Without Saving',
    continueEditing: 'Continue Editing',
    updateSuccess: 'Video updated successfully',
    updateFailed: 'Failed to update video',
    thumbnailUploadSuccess: 'Thumbnail uploaded successfully',
    thumbnailUploadFailed: 'Failed to upload thumbnail',
    // Video Player Controls
    play: 'Play',
    pause: 'Pause',
    mute: 'Mute',
    unmute: 'Unmute',
    fullscreen: 'Fullscreen',
    exitFullscreen: 'Exit Fullscreen',
    quality: 'Quality',
    speed: 'Speed',
    currentTime: 'Current Time',
    totalDuration: 'Total Duration',
    // Video Duration Filters
    shortVideos: 'Short (≤3 min)',
    standardVideos: 'Standard (3-20 min)',
    longVideos: 'Long (>20 min)',
  },

  search: {
    searchPlaceholder: 'Search videos, channels, and more...',
    searchResults: 'Search Results',
    searchVideos: 'Videos',
    searchChannels: 'Channels',
    searchPlaylists: 'Playlists',
    noSearchResults: 'No results found for your search',
    recentSearches: 'Recent Searches',
    clearSearchHistory: 'Clear Search History',
    trending: 'Trending',
    searchPageTitle: 'Search Videos',
    searchButton: 'Search',
    searching: 'Searching...',
    foundResults: 'Found {count} results',
    foundResultsFor: 'Found {count} results for "{query}"',
    activeFilters: 'Active filters:',
    category: 'Category',
    language: 'Language',
    duration: 'Duration',
    dateFrom: 'From',
    dateTo: 'To',
    noResultsTitle: 'No results found',
    noResultsMessage: 'Try adjusting your search terms or filters',
    clearFilters: 'Clear Filters',
    filters: 'Filters',
    active: 'Active',
    applyFilters: 'Apply Filters',
    clear: 'Clear',
    uploadDate: 'Upload Date',
    sortBy: 'Sort By',
    allCategories: 'All Categories',
    islamicLectures: 'Islamic Lectures',
    quranRecitation: 'Quran Recitation',
    islamicHistory: 'Islamic History',
    prayerGuidance: 'Prayer Guidance',
    islamicStories: 'Islamic Stories',
    scholarlyDiscussions: 'Scholarly Discussions',
    allLanguages: 'All Languages',
    english: 'English',
    arabic: 'Arabic',
    anyDuration: 'Any Duration',
    short: 'Short (< 4 minutes)',
    medium: 'Medium (4-20 minutes)',
    long: 'Long (> 20 minutes)',
    relevance: 'Relevance',
    uploadDateSort: 'Upload Date',
    viewCount: 'View Count',
    likes: 'Likes',
    descending: 'Descending',
    ascending: 'Ascending',
    trendingVideos: 'Trending Videos',
    lastHour: 'Last Hour',
    today: 'Today',
    thisWeek: 'This Week',
    thisMonth: 'This Month',
    noTrendingVideos: 'No trending videos found',
    checkBackLater: 'Check back later for trending content',
  },

  upload: {
    uploadTitle: 'Upload Video',
    uploadVideo: 'Upload Video',
    selectFile: 'Select File',
    dragDropFiles: 'Drag and drop files here',
    videoDetails: 'Video Details',
    uploading: 'Uploading...',
    uploadComplete: 'Upload Complete',
    uploadFailed: 'Upload Failed',
    uploadProgress: 'Upload Progress',
    processingVideo: 'Processing Video...',
    preparing: 'Preparing upload...',
    makePublic: 'Make this video public',
    cancelUpload: 'Cancel Upload',
    dropHere: 'Drop your video here',
    supports: 'Supports',
    max: 'Max',
    shareDescription: 'Share your knowledge and inspiration with the Ummah',
    shareWithCommunity: 'Share your content with the Al Ummah Now community',
    videoUploadedMessage: 'Your video has been uploaded successfully!',
    viewVideo: 'View Video',
    uploadAnother: 'Upload Another',
    waitingMessage: 'Your video is being processed. This may take a few minutes...',
    percentComplete: '% complete',
    guidelines: 'Upload Guidelines',
    guidelineIslamicValues: 'Content must align with Islamic values and teachings',
    guidelineMaxSize: 'Maximum file size: 5GB',
    guidelineFormats: 'Supported formats: MP4, WebM, MOV',
    guidelineCopyright: 'Ensure you have the right to upload this content',
    guidelineProcessing: 'Processing may take several minutes depending on video length',
    guidelineThumbnails: 'Custom thumbnails can be added after upload',
    // TUS upload states
    paused: 'Paused',
    processing: 'Processing...',
    complete: 'Upload Complete',
    failed: 'Upload Failed',
    pause: 'Pause',
    resume: 'Resume',
    cancel: 'Cancel',
    retry: 'Retry Upload',
    speed: 'Upload Speed',
    timeRemaining: 'Time Remaining',
    bytesUploaded: 'Uploaded',
  },

  batch: {
    batchUpload: 'Batch Upload',
    selectFiles: 'Select Files',
    selectedFiles: 'Selected Files',
    filesSelected: '{count} files selected',
    dragDropMultiple: 'Drag and drop multiple video files here (1-15 files)',
    clickToBrowse: 'Or click to browse',
    fileLimit: '1-15 video files (MP4, MOV, AVI, WebM)',
    removeFile: 'Remove file',
    sharedMetadata: 'Shared Metadata',
    individualMetadata: 'Individual Metadata',
    applyToAll: 'Apply to All',
    copyToAll: 'Copy to All',
    resetToShared: 'Reset to Shared',
    autoNumbering: 'Auto-Numbering',
    enableAutoNumbering: 'Enable auto-numbering in titles',
    numberingPattern: 'Pattern: Use {{n}} for number (e.g., "Episode {{n}}")',
    uploadQueue: 'Upload Queue',
    queueEmpty: 'No videos in queue',
    addVideos: 'Add Videos',
    startUploads: 'Start Uploads',
    pauseAll: 'Pause All',
    resumeAll: 'Resume All',
    cancelAll: 'Cancel All',
    retryFailed: 'Retry Failed',
    overallProgress: 'Overall Progress',
    completed: 'Completed',
    pending: 'Pending',
    uploading: 'Uploading',
    paused: 'Paused',
    cancelled: 'Cancelled',
    eta: 'ETA',
    overallEta: 'Overall ETA',
    concurrency: 'Concurrency',
    concurrentUploads: 'Concurrent Uploads',
    uploadSpeed: 'Upload Speed',
    totalProgress: 'Total Progress',
    ofCompleted: '{completed} of {total} completed',
    errors: {
      tooFewFiles: 'Please select at least 1 video file',
      tooManyFiles: 'Maximum 15 files allowed. You selected {count} files.',
      invalidFileType: 'Invalid file type: {filename}. Only video files are allowed.',
      fileTooLarge: 'File too large: {filename} ({size})',
      initiateFailed: 'Failed to initialize batch upload. Please try again.',
      uploadFailed: 'Upload failed for {filename}',
    },
  },

  schedule: {
    publishImmediately: 'Publish immediately',
    scheduleLater: 'Schedule for later',
    selectDateTime: 'Select date and time',
    timezone: 'Timezone',
    selectTimezone: 'Select your timezone',
    scheduled: 'Scheduled',
    publishNow: 'Publish Now',
    cancelSchedule: 'Cancel Schedule',
    editSchedule: 'Edit Schedule',
    scheduledFor: 'Scheduled for',
    scheduledVideos: 'Scheduled Videos',
    myScheduledVideos: 'My Scheduled Videos',
    noScheduledVideos: 'No scheduled videos',
    noScheduledVideosMessage: 'You don\'t have any videos scheduled for publication.',
    videoWillBePublished: 'This video will be published on',
    atTime: 'at',
    inYourTimezone: 'in your timezone',
    schedulingOptions: 'Scheduling Options',
    notifyOnPublish: 'Notify subscribers when published',
    goesLiveIn: 'Goes live in',
    dateAndTime: 'Date and Time',
    selectDate: 'Select date',
    selectTime: 'Select time',
    minimumScheduleTime: 'Videos must be scheduled at least 5 minutes in advance',
    maximumScheduleTime: 'Videos cannot be scheduled more than 1 year in advance',
    invalidScheduleTime: 'Invalid schedule time',
    scheduleConflict: 'This time slot is already taken',
    scheduleSuccess: 'Video scheduled successfully',
    scheduleFailed: 'Failed to schedule video',
    scheduleUpdated: 'Schedule updated successfully',
    scheduleCancelled: 'Schedule cancelled successfully',
    publishingNow: 'Publishing now...',
    publishedSuccessfully: 'Video published successfully',
    // Status labels
    statusScheduled: 'Scheduled',
    statusPublishing: 'Publishing...',
    statusPublished: 'Published',
    statusFailed: 'Failed',
    statusCancelled: 'Cancelled',
    // Filter and sort
    filterByStatus: 'Filter by status',
    allStatuses: 'All Statuses',
    sortByDate: 'Sort by date',
    oldestFirst: 'Oldest first',
    newestFirst: 'Newest first',
    // Actions
    viewVideo: 'View Video',
    editVideo: 'Edit Video',
    deleteVideo: 'Delete Video',
    // Time remaining
    timeRemaining: 'Time remaining',
    publishesIn: 'Publishes in',
    publishesSoon: 'Publishes soon',
    days: 'days',
    hours: 'hours',
    minutes: 'minutes',
    seconds: 'seconds',
    // Empty states
    createFirstScheduledVideo: 'Schedule your first video to publish it automatically',
    scheduleAVideo: 'Schedule a Video',
    // Confirmations
    confirmCancelSchedule: 'Are you sure you want to cancel this scheduled publication?',
    confirmPublishNow: 'Are you sure you want to publish this video now?',
    // Preview
    schedulePreview: 'Schedule Preview',
    willBePublishedOn: 'Will be published on',
    localTime: 'Local time',
    utcTime: 'UTC time',
    // Calendar View
    views: {
      list: 'List',
      calendar: 'Calendar',
    },
    calendar: {
      month: 'Month',
      week: 'Week',
      day: 'Day',
      today: 'Today',
      previous: 'Previous',
      next: 'Next',
      calendarType: 'Calendar Type',
      gregorian: 'Gregorian',
      hijri: 'Hijri (Islamic)',
      noEvents: 'No scheduled videos for this period',
      scheduledFor: 'Scheduled for',
      loadingEvents: 'Loading scheduled videos...',
      errorLoadingEvents: 'Error Loading Videos',
      unknownError: 'An unknown error occurred',
      noScheduledVideos: 'No Scheduled Videos',
      noScheduledVideosThisPeriod: 'No scheduled videos for this period. Try selecting a different date range.',
    },
    hijriMonths: {
      1: 'Muharram',
      2: 'Safar',
      3: 'Rabi al-Awwal',
      4: 'Rabi al-Thani',
      5: 'Jumada al-Awwal',
      6: 'Jumada al-Thani',
      7: 'Rajab',
      8: 'Shaban',
      9: 'Ramadan',
      10: 'Shawwal',
      11: 'Dhul-Qadah',
      12: 'Dhul-Hijjah',
    },
  },

  analytics: {
    analyticsTitle: 'Analytics',
    overview: 'Overview',
    views: 'Views',
    watchTime: 'Watch Time',
    subscribers: 'Subscribers',
    revenue: 'Revenue',
    topVideos: 'Top Videos',
    topCountries: 'Top Countries',
    topDevices: 'Top Devices',
    realtimeViews: 'Realtime Views',
    last28Days: 'Last 28 Days',
    last7Days: 'Last 7 Days',
    today: 'Today',
    demoMode: 'Demo Mode',
    demoMessage: 'This is a demonstration of the analytics dashboard with sample data',
    demoModeMessage: 'This is a demonstration of the analytics dashboard. Please log in to view your actual analytics.',
    trackPerformance: 'Track your channel performance and audience engagement',
  },

  time: {
    allTime: 'All Time',
    hours: 'hours',
    minutes: 'minutes',
  },

  subscriptions: {
    subscriptionsTitle: 'Subscriptions',
    mySubscriptions: 'My Subscriptions',
    manage: 'Manage',
    notificationSettings: 'Notification Settings',
    subscriptionFeed: 'Subscription Feed',
    noSubscriptions: 'No subscriptions yet',
    loginToSeeVideos: 'Log in to see the latest videos from channels you\'ve subscribed to.',
    latestVideos: 'Latest videos from your subscribed channels',
    lastUpdated: 'Last updated:',
    refresh: 'Refresh',
    loadingFeed: 'Loading your subscription feed...',
    noVideosInFeed: 'No videos in your feed',
    subscribeToSeeVideos: 'Subscribe to channels to see their latest videos here.',
    discoverChannels: 'Discover Channels',
    loadMoreComingSoon: 'Load More Videos (Coming Soon)',
    showingVideosFrom: 'Showing {count} videos from your subscriptions.',
    wantMore: 'Want to see more? Subscribe to more channels!',
  },

  feed: {
    feedTitle: 'Feed',
    home: 'Home',
    trending: 'Trending',
    subscriptions: 'Subscriptions',
    history: 'History',
    watchLater: 'Watch Later',
    likedVideos: 'Liked Videos',
  },

  moderation: {
    moderationTitle: 'Moderation',
    reports: 'Reports',
    reviewReport: 'Review Report',
    approve: 'Approve',
    reject: 'Reject',
    takeAction: 'Take Action',
    reportReason: 'Report Reason',
    reportStatus: 'Status',
    reportedBy: 'Reported By',
    reportDate: 'Report Date',
    moderationDashboard: 'Moderation Dashboard',
    dashboardSubtitle: 'Review and take action on reported content',
    totalReports: 'Total Reports',
    pendingReports: 'Pending Reports',
    allTime: 'All Time',
    needsAttention: 'Needs Attention',
    actions24h: 'Actions (24h)',
    recentActivity: 'Recent Activity',
    avgResponseTime: 'Avg. Response Time',
    meanResolution: 'Mean Resolution',
    filterReports: 'Filter Reports',
    priority: 'Priority',
    contentType: 'Content Type',
    recentReports: 'Recent Reports',
    reportsPendingAction: 'Reports Pending Action',
    noReportsMatch: 'No reports match your current filters',
    accessDenied: 'Access Denied',
    backToDashboard: 'Back to Dashboard',
    reportDetails: 'Report Details',
    reviewReportMessage: 'Review the report and take appropriate action',
    reportReasonLabel: 'Report Reason',
    descriptionLabel: 'Description',
    reportedByLabel: 'Reported By',
    reportDateLabel: 'Report Date',
    assignedToLabel: 'Assigned To',
    reason: 'Reason',
    description: 'Description',
    assignedTo: 'Assigned to',
    resolutionNotesLabel: 'Resolution Notes',
    reportedContentLabel: 'Reported Content',
    viewOriginalContent: 'View Original Content',
    chooseAction: 'Choose an action',
    takeModerationAction: 'Take Moderation Action',
    alreadyResolved: 'This report has already been resolved',
    actionType: 'Action Type',
    selectAction: 'Select an action',
    approveContent: 'Approve Content',
    removeContent: 'Remove Content',
    hideContent: 'Hide Content',
    warnUser: 'Warn User',
    restrictUser: 'Restrict User',
    banUser: 'Ban User',
    dismissReport: 'Dismiss Report',
    flagForReview: 'Flag for Review',
    briefReason: 'Provide a brief reason for your action',
    notesOptional: 'Notes (optional)',
    additionalNotes: 'Add any additional notes about your decision',
    takingAction: 'Taking action...',
    actionHistory: 'Action History',
    viewAllReports: 'View All Reports',
    userManagement: 'User Management',
    status: {
      pending: 'Pending',
      inReview: 'In Review',
      resolved: 'Resolved',
      rejected: 'Rejected',
      escalated: 'Escalated',
      allStatuses: 'All Statuses',
      dismissed: 'Dismissed',
    },
    priorityEnum: {
      urgent: 'Urgent',
      high: 'High',
      medium: 'Medium',
      low: 'Low',
      allPriorities: 'All Priorities',
    },
    contentTypeEnum: {
      video: 'Video',
      comment: 'Comment',
      channel: 'Channel',
      playlist: 'Playlist',
      allTypes: 'All Types',
    },
    reportReasonEnum: {
      inappropriateContent: 'Inappropriate Content',
      spam: 'Spam',
      harassment: 'Harassment',
      violence: 'Violence',
      hateSpeech: 'Hate Speech',
      falseInformation: 'False Information',
      copyright: 'Copyright Violation',
      privacyViolation: 'Privacy Violation',
      other: 'Other',
    },
  },

  auth: {
    login: 'Login',
    logout: 'Logout',
    signUp: 'Sign Up',
    forgotPassword: 'Forgot Password?',
    resetPassword: 'Reset Password',
    emailAddress: 'Email Address',
    password: 'Password',
    confirmPassword: 'Confirm Password',
    rememberMe: 'Remember Me',
    loginSuccess: 'Login successful',
    logoutSuccess: 'Logout successful',
    loginFailed: 'Login failed',
  },

  errors: {
    genericError: 'An error occurred. Please try again.',
    networkError: 'Network error. Please check your connection.',
    notFound: 'Page not found',
    notFoundDescription: 'Sorry, the page you are looking for does not exist. It might have been removed or the URL might be incorrect.',
    unauthorized: 'Unauthorized. Please login.',
    forbidden: 'Access forbidden',
    serverError: 'Server error. Please try again later.',
    validationError: 'Validation error',
    uploadError: 'Upload failed. Please try again.',
    uploadFailed: 'Upload failed',
    sessionExpired: 'Session expired. Please login again.',
    authRequired: 'Authentication required',
    channelRequired: 'No active channel selected. Please select a channel first.',
    failedToLoad: 'Failed to load. Please try again.',
    failedToSave: 'Failed to save. Please try again.',
    failedToDelete: 'Failed to delete. Please try again.',
    failedToUpdate: 'Failed to update. Please try again.',
    failedToCreate: 'Failed to create. Please try again.',
    failedToSwitch: 'Failed to switch. Please try again.',
    failedToProcess: 'Failed to process. Please try again.',
    invalidFile: 'Invalid file type.',
    fileTooLarge: 'File is too large.',
    reportReasonRequired: 'Please select a reason for reporting this content.',
    loginRequired: 'You must be logged in to perform this action.',
  },

  success: {
    saved: 'Saved successfully',
    deleted: 'Deleted successfully',
    uploaded: 'Uploaded successfully',
    updated: 'Updated successfully',
    created: 'Created successfully',
  },

  validation: {
    required: 'This field is required',
    invalidEmail: 'Invalid email address',
    invalidUrl: 'Invalid URL',
    minLength: 'Minimum length is {min} characters',
    maxLength: 'Maximum length is {max} characters',
    passwordMismatch: 'Passwords do not match',
  },

  comments: {
    commentsCount: 'Comments ({count})',
    addComment: 'Add a comment...',
    cancel: 'Cancel',
    comment: 'Comment',
    commenting: 'Commenting...',
    signInToComment: 'Sign in to join the conversation',
    signIn: 'Sign In to Comment',
    noComments: 'No comments yet. Be the first to comment!',
    liveUpdates: 'Comments update in real-time',
    live: 'Live',
    offline: 'Offline',
  },

  community: {
    community: 'Community',
    managePosts: 'Manage Posts',
    newPost: 'New Post',
    searchPosts: 'Search posts...',
    allTypes: 'All Types',
    text: 'Text',
    image: 'Image',
    poll: 'Poll',
    quiz: 'Quiz',
    video: 'Video',
    published: 'Published',
    scheduled: 'Scheduled',
    drafts: 'Drafts',
    noResultsFound: 'No results found',
    noPostsYet: 'No posts yet',
    createFirstPost: 'Create your first post',
  },

  notifications: {
    notificationCenter: 'Notification Center',
    new: 'new',
    view: 'View',
    markAsRead: 'Mark as Read',
    markAllAsRead: 'Mark All as Read',
    noNotifications: 'No notifications',
    viewAll: 'View All',
    testNotification: 'Test Notification',
    demoFeatures: 'Demo Features',
    likeNotification: 'New Like',
    commentNotification: 'New Comment',
    subscriptionNotification: 'New Subscriber',
    uploadNotification: 'Upload Complete',
    systemNotification: 'System Update',
    likeMessage: 'Someone liked your video "Amazing Islamic Lecture"',
    commentMessage: 'New comment on your video: "Mashallah, very beneficial!"',
    subscriptionMessage: 'You have a new subscriber!',
    uploadMessage: 'Your video "New Upload" has been successfully processed',
    systemMessage: 'Welcome to Al Ummah Now! Start exploring Islamic content.',
  },

  subtitle: {
    title: 'Subtitles',
    subtitles: 'Subtitles / Captions',
    subtitlesDescription: 'Upload subtitle files in multiple languages',
    upload: 'Upload',
    language: 'Language',
    default: 'Default',
    delete: 'Delete',
    settings: 'Settings',
    fontSize: 'Font Size',
    off: 'Off',
    preview: 'Subtitle Preview',
    showingFirst5: 'Showing first 5 cues',
    warnings: 'Potential Issues Detected',
    moreCues: '+{count} more cues',
    noIssues: 'No issues detected. Subtitles look good!',
    uploadSubtitle: 'Upload Subtitle',
    selectLanguage: 'Select Language',
    chooseLanguage: 'Choose a language',
    selectFile: 'Select File',
    subtitleFile: 'Subtitle File',
    uploadProgress: 'Upload Progress',
    uploading: 'Uploading...',
    uploadSuccess: 'Subtitle uploaded successfully!',
    uploadError: 'An error occurred while uploading',
    uploadFailed: 'Failed to upload subtitle',
    setAsDefault: 'Set as default subtitle track',
    setDefault: 'Set Default',
    fileSize: 'File Size',
    status: 'Status',
    uploadedAt: 'Uploaded At',
    actions: 'Actions',
    noSubtitles: 'No subtitles uploaded yet',
    noSubtitlesDescription: 'Add subtitle tracks to make your video accessible in multiple languages',
    uploadFirstSubtitle: 'Upload First Subtitle',
    addSubtitle: 'Add Subtitle',
    validationErrors: 'Validation Errors',
    validating: 'Validating subtitle file...',
    validationFailed: 'Failed to validate subtitle file',
    completeAllFields: 'Please complete all fields',
    dragDropOrClick: 'Drag and drop or click to browse',
    supportedFormats: 'Supported: SRT, VTT, SBV (Max 2MB)',
    uploadFileArea: 'Upload subtitle file',
    confirmDelete: 'Delete Subtitle?',
    confirmDeleteDescription: 'Are you sure you want to delete this subtitle track? This action cannot be undone.',
    deleteSubtitle: 'Delete subtitle',
    statusActive: 'Active',
    statusProcessing: 'Processing',
    statusFailed: 'Failed',
  },

  sections: {
    // Video Sections
    recentVideos: 'Recent Uploads',
    popularVideos: 'Popular Videos',
    trendingVideos: 'Trending',
    recommendedVideos: 'Recommended for You',
    noRecentVideos: 'No recent videos available',
    noPopularVideos: 'No popular videos available',
    noTrendingVideos: 'No trending videos available',
    noRecommendedVideos: 'No recommended videos available',
    // Time Periods
    periodDaily: 'Today',
    periodWeekly: 'This Week',
    periodMonthly: 'This Month',
    periodAll: 'All Time',
    // Strategies
    strategyPopular: 'Popular',
    strategyCategoryBased: 'Similar',
    strategyRandom: 'Discover',
    basedOn: 'Based on',
    // Common
    loadMore: 'Load More',
    collapse: 'Collapse',
    expand: 'Expand',
  },

  uploadDefaults: {
    title: 'Upload Defaults',
    subtitle: 'Create templates to save time when uploading videos. Use variables to auto-fill metadata.',
    createTemplate: 'Create New Template',
    createFirstTemplate: 'Create First Template',
    noTemplates: 'No templates yet',
    noTemplatesMessage: 'Create your first template to speed up video uploads',
    templateName: 'Template Name',
    templateNameEnglish: 'Template Name (English)',
    templateNameArabic: 'Template Name (Arabic)',
    templateDescription: 'Template Description',
    templateDescriptionPlaceholder: 'Optional description for this template',
    setAsDefault: 'Set as default template',
    defaultBadge: 'Default',
    category: 'Category',
    selectCategory: 'Select a category',
    used: 'Used',
    times: 'times',
    createNewTemplate: 'Create New Template',
    editTemplate: 'Edit Template',
    deleteTemplate: 'Delete Template',
    confirmDelete: 'Are you sure you want to delete this template?',
    deleteSuccess: 'Template deleted successfully',
    deleteFailed: 'Failed to delete template',
    setDefaultSuccess: 'Default template updated',
    setDefaultFailed: 'Failed to set default template',
    // Template Editor
    templateInformation: 'Template Information',
    templateNameRequired: 'Template name is required',
    titleRequired: 'Title is required',
    videoMetadata: 'Video Metadata',
    titleEnglish: 'Title (English)',
    titleArabic: 'Title (Arabic)',
    descriptionEnglish: 'Description (English)',
    descriptionArabic: 'Description (Arabic)',
    useVariables: 'Use variables like {{date}}',
    useVariablesArabic: 'استخدم المتغيرات مثل {{date}}',
    tags: 'Tags',
    tagsPlaceholder: 'Separate tags with commas',
    tagsExample: 'e.g., Islamic, Lecture, Quran',
    visibility: 'Visibility',
    public: 'Public',
    private: 'Private',
    unlisted: 'Unlisted',
    language: 'Language',
    english: 'English',
    arabic: 'Arabic',
    saving: 'Saving...',
    saveFailed: 'Failed to save template',
    saveError: 'An error occurred while saving',
    // Template Selector
    uploadTemplate: 'Upload Template',
    noneManualEntry: 'None (Manual Entry)',
    defaultTemplate: '(Default)',
    templateApplied: 'Template applied to form below',
    loadTemplatesFailed: 'Failed to load templates',
    loadTemplatesError: 'An error occurred while loading',
    // Variable Prompts
    enterVariableValues: 'Enter Variable Values',
    templateRequiresInputs: 'This template requires some inputs',
    episodeNumber: 'Episode Number',
    episodeNumberPlaceholder: 'e.g., 12',
    seasonNumber: 'Season Number',
    seasonNumberPlaceholder: 'e.g., 2',
    seriesName: 'Series Name',
    seriesNamePlaceholder: 'e.g., Islamic History',
    partNumber: 'Part Number',
    partNumberPlaceholder: 'e.g., 3',
    surahName: 'Surah Name/Number',
    surahNamePlaceholder: 'e.g., Al-Fatiha or 1',
    ayahRange: 'Ayah Range',
    ayahRangePlaceholder: 'e.g., 1-7',
    juzNumber: 'Juz Number',
    juzNumberPlaceholder: 'e.g., 1',
    // Variable Guide
    variableGuide: 'Variable Guide',
    variables: 'variables',
    requiresInput: 'Requires input',
    example: 'Example:',
    copy: 'Copy',
    copied: 'Copied',
    variableUsageNote: 'Use these variables in your title or description. They will be automatically replaced with actual values when you upload a video.',
    // Variable Categories
    categoryDateTime: 'Date & Time',
    categoryIslamic: 'Islamic Calendar',
    categoryEpisode: 'Episode & Series',
    categoryQuran: 'Quran References',
    categoryChannel: 'Channel Info',
    categoryVideo: 'Video Info',
  },
};
